import React, { useState, useEffect } from 'react';
import productionService from '../../../services/productionService';
import animalService from '../../../services/animalService';
import financeService from '../../../services/financeService';
import leftoverService from '../../../services/leftoverService';
import authService from '../../../services/authServices';

const PAGE_SIZE = 10;

const today = new Date().toISOString().split('T')[0];
const todayFormatted = new Date().toLocaleDateString('en-US', {
  year: 'numeric', month: 'long', day: 'numeric'
});

const formatDate = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const getYesterday = () => {
  const d = new Date(); d.setDate(d.getDate() - 1);
  return d.toISOString().split('T')[0];
};
const getWeekStart = () => {
  const d = new Date(); d.setDate(d.getDate() - d.getDay());
  return d.toISOString().split('T')[0];
};
const getMonthStart = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().split('T')[0];
};

const groupByAnimal = (records) => {
  const grouped = {};
  records.forEach(p => {
    const key = p.tag_number;
    if (!grouped[key]) {
      grouped[key] = { tag_number: p.tag_number, animal_name: p.animal_name, animal_type: p.animal_type, morning: null, evening: null };
    }
    if (p.session === 'Morning') grouped[key].morning = p;
    else grouped[key].evening = p;
  });
  return Object.values(grouped);
};

// ── Reusable Pagination Control ───────────────────────────────────────────────
const Pagination = ({ currentPage, totalPages, onPageChange, totalItems, pageSize, label = 'animals' }) => {
  if (totalPages <= 1) return null;
  const start = (currentPage - 1) * pageSize + 1;
  const end   = Math.min(currentPage * pageSize, totalItems);
  return (
    <div style={paginationStyles.wrapper}>
      <span style={paginationStyles.info}>Showing {start}–{end} of {totalItems} {label}</span>
      <div style={paginationStyles.controls}>
        <button onClick={() => onPageChange(1)}               disabled={currentPage === 1}          style={{ ...paginationStyles.btn, ...(currentPage === 1          ? paginationStyles.btnDisabled : {}) }}>«</button>
        <button onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1}          style={{ ...paginationStyles.btn, ...(currentPage === 1          ? paginationStyles.btnDisabled : {}) }}>‹ Prev</button>
        <span style={paginationStyles.pageIndicator}>Page {currentPage} of {totalPages}</span>
        <button onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages} style={{ ...paginationStyles.btn, ...(currentPage === totalPages ? paginationStyles.btnDisabled : {}) }}>Next ›</button>
        <button onClick={() => onPageChange(totalPages)}      disabled={currentPage === totalPages} style={{ ...paginationStyles.btn, ...(currentPage === totalPages ? paginationStyles.btnDisabled : {}) }}>»</button>
      </div>
    </div>
  );
};

// ── Individual Table (Today / Yesterday) ──────────────────────────────────────
const IndividualTable = ({ records, isAdmin, todayDate, onEdit, onDelete, page, setPage }) => {
  const grouped = groupByAnimal(records);
  const grandTotal = records.reduce((sum, p) =>
    sum + (p.session === 'Morning' ? parseFloat(p.morning_production || 0) : parseFloat(p.evening_production || 0)), 0);

  const totalPages    = Math.ceil(grouped.length / PAGE_SIZE);
  const paginated     = grouped.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  if (grouped.length === 0) return <div style={styles.emptySection}>🥛 No records found</div>;

  return (
    <>
      <div style={{ padding: '10px 16px', background: '#f8f9fa', borderBottom: '1px solid #e0e0e0', textAlign: 'right', fontSize: '13px', fontWeight: '600', color: '#27ae60' }}>
        Total: {grandTotal.toFixed(1)} L &nbsp;|&nbsp; {grouped.length} animals
      </div>
      <table style={styles.table}>
        <thead>
          <tr style={styles.headerRow}>
            <th style={styles.th}>#</th>
            <th style={styles.th}>Tag #</th>
            <th style={styles.th}>Animal Name</th>
            <th style={styles.th}>Type</th>
            <th style={{ ...styles.th, background: '#fff8e1', color: '#856404' }}>🌅 Morning (L)</th>
            <th style={{ ...styles.th, background: '#e8f4fd', color: '#1565c0' }}>🌙 Evening (L)</th>
            <th style={styles.th}>Total (L)</th>
          </tr>
        </thead>
        <tbody>
          {paginated.map((row, idx) => {
            const morningVal   = row.morning ? parseFloat(row.morning.morning_production || 0) : null;
            const eveningVal   = row.evening ? parseFloat(row.evening.evening_production || 0) : null;
            const rowTotal     = (morningVal || 0) + (eveningVal || 0);
            const canEditMorning = isAdmin || (row.morning?.date?.split('T')[0] === todayDate);
            const canEditEvening = isAdmin || (row.evening?.date?.split('T')[0] === todayDate);

            return (
              <tr key={row.tag_number} style={styles.row}>
                <td style={{ ...styles.td, color: '#95a5a6', fontSize: '13px', width: '40px' }}>
                  {(page - 1) * PAGE_SIZE + idx + 1}
                </td>
                <td style={styles.td}><span style={styles.tag}>{row.tag_number}</span></td>
                <td style={styles.td}><strong>{row.animal_name}</strong></td>
                <td style={styles.td}>
                  <span style={animalTypeBadge(row.animal_type)}>{row.animal_type || '-'}</span>
                </td>
                <td style={{ ...styles.td, background: '#fffde7' }}>
                  {morningVal !== null ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <strong style={{ color: '#856404' }}>{morningVal.toFixed(1)} L</strong>
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <button onClick={() => onEdit(row.morning)}   style={{ ...styles.editButton,   opacity: canEditMorning ? 1 : 0.4, cursor: canEditMorning ? 'pointer' : 'not-allowed' }}>✏️</button>
                        <button onClick={() => onDelete(row.morning)} style={{ ...styles.deleteButton, opacity: canEditMorning ? 1 : 0.4, cursor: canEditMorning ? 'pointer' : 'not-allowed' }}>🗑️</button>
                      </div>
                    </div>
                  ) : <span style={{ color: '#bbb', fontSize: '13px' }}>— not entered</span>}
                </td>
                <td style={{ ...styles.td, background: '#e8f4fd' }}>
                  {eveningVal !== null ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <strong style={{ color: '#1565c0' }}>{eveningVal.toFixed(1)} L</strong>
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <button onClick={() => onEdit(row.evening)}   style={{ ...styles.editButton,   opacity: canEditEvening ? 1 : 0.4, cursor: canEditEvening ? 'pointer' : 'not-allowed' }}>✏️</button>
                        <button onClick={() => onDelete(row.evening)} style={{ ...styles.deleteButton, opacity: canEditEvening ? 1 : 0.4, cursor: canEditEvening ? 'pointer' : 'not-allowed' }}>🗑️</button>
                      </div>
                    </div>
                  ) : <span style={{ color: '#bbb', fontSize: '13px' }}>— not entered</span>}
                </td>
                <td style={styles.td}>
                  <strong style={{ color: '#27ae60', fontSize: '15px' }}>{rowTotal.toFixed(1)} L</strong>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <Pagination
        currentPage={page}
        totalPages={totalPages}
        onPageChange={setPage}
        totalItems={grouped.length}
        pageSize={PAGE_SIZE}
      />
    </>
  );
};

// ── Cumulative Table (This Week / This Month) ─────────────────────────────────
const CumulativeTable = ({ records, page, setPage }) => {
  const grouped = {};
  records.forEach(p => {
    const key = p.tag_number;
    if (!grouped[key]) {
      grouped[key] = { tag_number: p.tag_number, animal_name: p.animal_name, animal_type: p.animal_type, morning: 0, evening: 0 };
    }
    if (p.session === 'Morning') grouped[key].morning += parseFloat(p.morning_production || 0);
    else                         grouped[key].evening += parseFloat(p.evening_production || 0);
  });

  const rows       = Object.values(grouped).map(r => ({ ...r, total: r.morning + r.evening })).sort((a, b) => b.total - a.total);
  const grandTotal = rows.reduce((sum, r) => sum + r.total, 0);
  const totalPages = Math.ceil(rows.length / PAGE_SIZE);
  const paginated  = rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  if (rows.length === 0) return <div style={styles.emptySection}>🥛 No records found</div>;

  return (
    <>
      <div style={{ padding: '10px 16px', background: '#f8f9fa', borderBottom: '1px solid #e0e0e0', textAlign: 'right', fontSize: '13px', fontWeight: '600', color: '#27ae60' }}>
        Total: {grandTotal.toFixed(1)} L &nbsp;|&nbsp; {rows.length} animals
      </div>
      <table style={styles.table}>
        <thead>
          <tr style={styles.headerRow}>
            <th style={styles.th}>#</th>
            <th style={styles.th}>Tag #</th>
            <th style={styles.th}>Animal Name</th>
            <th style={styles.th}>Type</th>
            <th style={{ ...styles.th, background: '#fff8e1', color: '#856404' }}>🌅 Morning (L)</th>
            <th style={{ ...styles.th, background: '#e8f4fd', color: '#1565c0' }}>🌙 Evening (L)</th>
            <th style={styles.th}>Total (L)</th>
          </tr>
        </thead>
        <tbody>
          {paginated.map((row, idx) => (
            <tr key={row.tag_number} style={styles.row}>
              <td style={{ ...styles.td, color: '#95a5a6', fontSize: '13px', width: '40px' }}>
                {(page - 1) * PAGE_SIZE + idx + 1}
              </td>
              <td style={styles.td}><span style={styles.tag}>{row.tag_number}</span></td>
              <td style={styles.td}><strong>{row.animal_name}</strong></td>
              <td style={styles.td}>
                <span style={animalTypeBadge(row.animal_type)}>{row.animal_type || '-'}</span>
              </td>
              <td style={{ ...styles.td, background: '#fffde7' }}>
                <strong style={{ color: '#856404' }}>{row.morning.toFixed(1)} L</strong>
              </td>
              <td style={{ ...styles.td, background: '#e8f4fd' }}>
                <strong style={{ color: '#1565c0' }}>{row.evening.toFixed(1)} L</strong>
              </td>
              <td style={styles.td}>
                <strong style={{ color: '#27ae60', fontSize: '15px' }}>{row.total.toFixed(1)} L</strong>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <Pagination
        currentPage={page}
        totalPages={totalPages}
        onPageChange={setPage}
        totalItems={rows.length}
        pageSize={PAGE_SIZE}
      />
    </>
  );
};

// ── Helpers ───────────────────────────────────────────────────────────────────
const animalTypeBadge = (type) => ({
  background: type === 'Cow' ? '#e3f2fd' : type === 'Buffalo' ? '#f3e5f5' : '#e8f5e9',
  color:      type === 'Cow' ? '#1565c0' : type === 'Buffalo' ? '#6a1b9a'  : '#1b5e20',
  padding: '3px 10px', borderRadius: '10px', fontSize: '12px', fontWeight: '600',
});

// ── Main Component ────────────────────────────────────────────────────────────
const ProductionTab = () => {
  const [productions,     setProductions]     = useState([]);
  const [todayProductions,setTodayProductions] = useState([]);
  const [milkingAnimals,  setMilkingAnimals]  = useState([]);
  const [todaySales,      setTodaySales]      = useState([]);
  const [carriedForward,  setCarriedForward]  = useState(0);
  const [totalLeftoverAvailable, setTotalLeftoverAvailable] = useState(0);
  const [splitSummary,    setSplitSummary]    = useState(null);
  const [loading,         setLoading]         = useState(true);
  const [error,           setError]           = useState(null);
  const [showBulkModal,   setShowBulkModal]   = useState(false);
  const [showEditModal,   setShowEditModal]   = useState(false);
  const [selectedProduction, setSelectedProduction] = useState(null);
  const [bulkSession,     setBulkSession]     = useState('');
  const [bulkDate,        setBulkDate]        = useState(today);
  const [bulkEntries,     setBulkEntries]     = useState([]);
  const [saving,          setSaving]          = useState(false);
  const [currentUser,     setCurrentUser]     = useState(null);
  const [editFormData,    setEditFormData]    = useState({ production: '' });
  const [activeTab,       setActiveTab]       = useState('today');

  // ── Per-tab pagination state ──────────────────────────────────────────────
  const [todayPage,     setTodayPage]     = useState(1);
  const [yesterdayPage, setYesterdayPage] = useState(1);
  const [weekPage,      setWeekPage]      = useState(1);
  const [monthPage,     setMonthPage]     = useState(1);

  useEffect(() => {
    const user = authService.getCurrentUser();
    setCurrentUser(user);
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true); setError(null);
      const [prodRes, animalsRes, salesRes] = await Promise.all([
        productionService.getAllProduction(),
        animalService.getMilkingAnimals(),
        financeService.getAllMilkSales(),
      ]);
      const allProductions = prodRes.data  || [];
      const allSales       = salesRes.data || [];
      setProductions(allProductions);
      setTodayProductions(allProductions.filter(p => p.date && p.date.split('T')[0] === today));
      setTodaySales(allSales.filter(s => {
        if (!s.sale_date) return false;
        const d = s.sale_date.includes('T') ? s.sale_date.split('T')[0] : s.sale_date.substring(0, 10);
        return d === today;
      }));
      setMilkingAnimals(animalsRes.data || []);
      // Reset all pages on fresh fetch
      setTodayPage(1); setYesterdayPage(1); setWeekPage(1); setMonthPage(1);

      try {
        const leftoverRes = await leftoverService.getSummary(today);
        setCarriedForward(parseFloat(leftoverRes.data?.carried_forward || 0));
        setTotalLeftoverAvailable(parseFloat(leftoverRes.data?.total_leftover_available || 0));
        const splitRes = await leftoverService.getSplitSummary(today);
        setSplitSummary(splitRes.data);
      } catch (err) {
        console.error('Could not load leftover data:', err);
        setCarriedForward(0); setSplitSummary(null);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Failed to load data. Please try again.');
    } finally { setLoading(false); }
  };

  const openBulkModal = (session) => {
    const entries = milkingAnimals
      .filter(a => a.type === 'Cow' || a.type === 'Buffalo')
      .map(a => ({ tag_number: a.tag_number, animal_name: a.name, animal_type: a.type, quantity: '' }));
    if (entries.length === 0) { alert('❌ No milking animals found!'); return; }
    setBulkSession(session); setBulkDate(today); setBulkEntries(entries); setShowBulkModal(true);
  };

  const handleQuantityChange = (index, value) => {
    const updated = [...bulkEntries]; updated[index].quantity = value; setBulkEntries(updated);
  };

  const handleSaveBulk = async () => {
    const toSave = bulkEntries.filter(e => e.quantity !== '' && parseFloat(e.quantity) > 0);
    if (toSave.length === 0) { alert('❌ Please enter quantity for at least one animal!'); return; }
    const isBackdate = bulkDate !== today;
    if (isBackdate) {
      const sevenDaysAgo = new Date(); sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      if (bulkDate < sevenDaysAgo.toISOString().split('T')[0]) { alert('❌ Cannot add production older than 7 days!'); return; }
    }
    setSaving(true);
    try {
      if (isBackdate) {
        await leftoverService.addBackdateProduction({ date: bulkDate, entries: toSave.map(e => ({ ...e, session: bulkSession })) });
        alert('✅ Backdate production saved and added to leftover!');
      } else {
        const alreadyEntered = todayProductions.filter(p => p.session === bulkSession).map(p => p.tag_number);
        let successCount = 0, errorCount = 0;
        for (const entry of toSave) {
          if (alreadyEntered.includes(entry.tag_number)) continue;
          try {
            await productionService.createProduction({
              tag_number: entry.tag_number, session: bulkSession, record_date: today, animal_type: entry.animal_type,
              morning_production: bulkSession === 'Morning' ? parseFloat(entry.quantity) : 0,
              evening_production: bulkSession === 'Evening' ? parseFloat(entry.quantity) : 0,
            });
            successCount++;
          } catch { errorCount++; }
        }
        alert(errorCount === 0 ? `✅ ${successCount} entries saved!` : `✅ ${successCount} saved, ❌ ${errorCount} failed.`);
      }
    } catch (error) {
      alert('❌ ' + (error.response?.data?.message || 'Failed to save production'));
    } finally {
      setSaving(false); setShowBulkModal(false); setBulkEntries([]); setBulkSession(''); setBulkDate(today);
      fetchData();
    }
  };

  const openEditModal = (production) => {
    const isAdmin  = currentUser?.role === 'businessman';
    const prodDate = production.date ? production.date.split('T')[0] : '';
    if (!isAdmin && prodDate !== today) { alert('❌ You can only edit today\'s entries.'); return; }
    setSelectedProduction(production);
    setEditFormData({ production: production.session === 'Morning' ? production.morning_production : production.evening_production });
    setShowEditModal(true);
  };

  const handleEditProduction = async (e) => {
    e.preventDefault();
    try {
      await productionService.updateProduction(selectedProduction.id, {
        morning_production: selectedProduction.session === 'Morning' ? parseFloat(editFormData.production) : undefined,
        evening_production: selectedProduction.session === 'Evening' ? parseFloat(editFormData.production) : undefined,
      });
      alert('✅ Production updated successfully!');
      setShowEditModal(false); setSelectedProduction(null); fetchData();
    } catch (error) { alert('❌ ' + (error.response?.data?.message || 'Failed to update production')); }
  };

  const handleDeleteProduction = async (production) => {
    const isAdmin  = currentUser?.role === 'businessman';
    const prodDate = production.date ? production.date.split('T')[0] : '';
    if (!isAdmin && prodDate !== today) { alert('❌ You can only delete today\'s entries.'); return; }
    if (!window.confirm(`Delete ${production.session} entry for ${production.animal_name}?`)) return;
    try {
      await productionService.deleteProduction(production.id);
      alert('✅ Deleted successfully!'); fetchData();
    } catch (error) { alert('❌ ' + (error.response?.data?.message || 'Failed to delete')); }
  };

  // ── Filtered records per tab ──────────────────────────────────────────────
  const yesterday  = getYesterday();
  const weekStart  = getWeekStart();
  const monthStart = getMonthStart();

  const todayRecs     = productions.filter(p => p.date && p.date.split('T')[0] === today);
  const yesterdayRecs = productions.filter(p => p.date && p.date.split('T')[0] === yesterday);
  const thisWeekRecs  = productions.filter(p => { const d = p.date?.split('T')[0] || ''; return d >= weekStart  && d <= today; });
  const thisMonthRecs = productions.filter(p => { const d = p.date?.split('T')[0] || ''; return d >= monthStart && d <= today; });

  // ── Today summary numbers ─────────────────────────────────────────────────
  const todayCowMorning     = todayRecs.filter(p => p.animal_type === 'Cow'     && p.session === 'Morning').reduce((s, p) => s + parseFloat(p.morning_production || 0), 0);
  const todayCowEvening     = todayRecs.filter(p => p.animal_type === 'Cow'     && p.session === 'Evening').reduce((s, p) => s + parseFloat(p.evening_production || 0), 0);
  const todayBuffaloMorning = todayRecs.filter(p => p.animal_type === 'Buffalo' && p.session === 'Morning').reduce((s, p) => s + parseFloat(p.morning_production || 0), 0);
  const todayBuffaloEvening = todayRecs.filter(p => p.animal_type === 'Buffalo' && p.session === 'Evening').reduce((s, p) => s + parseFloat(p.evening_production || 0), 0);
  const totalCow      = todayCowMorning + todayCowEvening;
  const totalBuffalo  = todayBuffaloMorning + todayBuffaloEvening;
  const grandTotal    = totalCow + totalBuffalo;
  const todayTotalSold = todaySales.filter(s => s.session !== 'Leftover').reduce((s, sale) => s + parseFloat(sale.quantity || 0), 0);
  const leftoverSold = todaySales.filter(s => s.session === 'Leftover').reduce((s, sale) => s + parseFloat(sale.quantity || 0), 0);
const totalAvailable = grandTotal + carriedForward;
const adjustedCarriedForward = Math.max(0, carriedForward - leftoverSold);
  const todayLeftover = grandTotal - todayTotalSold;
  // Remaining milk including opening stock shown on screen (Opening + Today Produced − Sold)
  const remainingMilk = totalLeftoverAvailable + grandTotal - todayTotalSold;

  const isAdmin = currentUser?.role === 'businessman';

  const tabs = [
    { key: 'today',     label: '📅 Today',      count: groupByAnimal(todayRecs).length },
    { key: 'yesterday', label: '📆 Yesterday',  count: groupByAnimal(yesterdayRecs).length },
    { key: 'week',      label: '📊 This Week',  count: groupByAnimal(thisWeekRecs).length },
    { key: 'month',     label: '📈 This Month', count: groupByAnimal(thisMonthRecs).length },
  ];

  if (loading) return (
    <div style={{ padding: '40px', textAlign: 'center' }}>
      <div style={{ fontSize: '48px', marginBottom: '20px' }}>⏳</div>
      <h2 style={{ color: '#666' }}>Loading production data...</h2>
    </div>
  );
  if (error) return (
    <div style={{ padding: '40px', textAlign: 'center' }}>
      <div style={{ fontSize: '48px', marginBottom: '20px' }}>⚠️</div>
      <h2 style={{ color: '#e74c3c', marginBottom: '10px' }}>Error</h2>
      <p style={{ color: '#666' }}>{error}</p>
      <button onClick={fetchData} style={styles.retryButton}>🔄 Retry</button>
    </div>
  );

  return (
    <div>
      {/* ── Header ── */}
      <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#2c3e50', marginBottom: '8px' }}>Production Records</h2>
          <p style={{ color: '#7f8c8d', fontSize: '14px' }}>Track daily milk production — {milkingAnimals.length} milking animals</p>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button onClick={() => openBulkModal('Morning')} style={styles.morningButton}>🌅 Morning Entry</button>
          <button onClick={() => openBulkModal('Evening')} style={styles.eveningButton}>🌙 Evening Entry</button>
        </div>
      </div>

      {/* ── Today's Summary Cards ── */}
      <div style={styles.todayContainer}>
        <h3 style={styles.todayTitle}>🥛 Today's Summary — {todayFormatted}</h3>
        <div style={styles.summaryGrid}>
          <div style={{ ...styles.summaryCard, background: '#e3f2fd', border: '2px solid #1565c020' }}>
            <div style={styles.summaryAnimalTitle}>🐄 Cow Milk</div>
            <div style={styles.summaryRow}><span style={styles.summaryLabel}>Morning:</span><span style={styles.summaryValue}>{todayCowMorning.toFixed(1)} L</span></div>
            <div style={styles.summaryRow}><span style={styles.summaryLabel}>Evening:</span><span style={styles.summaryValue}>{todayCowEvening.toFixed(1)} L</span></div>
            <div style={styles.summaryTotal}>Total: {totalCow.toFixed(1)} L</div>
          </div>
          <div style={{ ...styles.summaryCard, background: '#f3e5f5', border: '2px solid #6a1b9a20' }}>
            <div style={styles.summaryAnimalTitle}>🐃 Buffalo Milk</div>
            <div style={styles.summaryRow}><span style={styles.summaryLabel}>Morning:</span><span style={styles.summaryValue}>{todayBuffaloMorning.toFixed(1)} L</span></div>
            <div style={styles.summaryRow}><span style={styles.summaryLabel}>Evening:</span><span style={styles.summaryValue}>{todayBuffaloEvening.toFixed(1)} L</span></div>
            <div style={styles.summaryTotal}>Total: {totalBuffalo.toFixed(1)} L</div>
          </div>
          <div style={{ ...styles.summaryCard, background: '#e8f5e9', border: '2px solid #1b5e2020' }}>
            <div style={styles.summaryAnimalTitle}>🥛 Grand Total</div>
            <div style={{ fontSize: '48px', fontWeight: '700', color: '#1b5e20', textAlign: 'center', margin: '10px 0' }}>{grandTotal.toFixed(1)}</div>
            <div style={{ textAlign: 'center', color: '#1b5e20', fontWeight: '600', fontSize: '16px' }}>Liters</div>
          </div>
        </div>

        {/* ── Milk Allocation ── */}
        <div style={{ marginTop: '20px', borderTop: '2px solid #e0e0e0', paddingTop: '20px' }}>
          <h4 style={{ fontSize: '16px', fontWeight: '700', color: '#2c3e50', marginBottom: '14px' }}>📊 Today's Milk Allocation</h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '14px' }}>
            <div style={{ background: '#e8f5e9', borderRadius: '10px', padding: '16px', textAlign: 'center', border: '2px solid #a5d6a7' }}>
              <div style={{ fontSize: '11px', color: '#555', textTransform: 'uppercase', fontWeight: '600', marginBottom: '6px' }}>Today Produced</div>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#1b5e20' }}>{grandTotal.toFixed(1)} L</div>
            </div>
            {totalLeftoverAvailable > 0 && (
              <div style={{ background: '#fff8e1', borderRadius: '10px', padding: '16px', textAlign: 'center', border: '2px solid #ffe082' }}>
                <div style={{ fontSize: '11px', color: '#555', textTransform: 'uppercase', fontWeight: '600', marginBottom: '6px' }}>Opening Milk Quantity Today</div>
                <div style={{ fontSize: '24px', fontWeight: '700', color: '#f57f17' }}>{totalLeftoverAvailable.toFixed(1)} L</div>
                <div style={{ fontSize: '10px', color: '#f57f17', marginTop: '2px', fontWeight: '600' }}>From previous days</div>
              </div>
            )}
            <div style={{ background: '#e3f2fd', borderRadius: '10px', padding: '16px', textAlign: 'center', border: '2px solid #90caf9' }}>
              <div style={{ fontSize: '11px', color: '#555', textTransform: 'uppercase', fontWeight: '600', marginBottom: '6px' }}>Total Sold TODAY</div>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#1565c0' }}>{todayTotalSold.toFixed(1)} L</div>
            </div>
            <div style={{
              background: remainingMilk > 0 ? '#fff3e0' : remainingMilk < 0 ? '#ffebee' : '#f8f9fa',
              borderRadius: '10px', padding: '16px', textAlign: 'center',
              border: `2px solid ${remainingMilk > 0 ? '#ffcc80' : remainingMilk < 0 ? '#ef9a9a' : '#e0e0e0'}`
            }}>
              <div style={{ fontSize: '11px', color: '#555', textTransform: 'uppercase', fontWeight: '600', marginBottom: '6px' }}>
                {remainingMilk < 0 ? '⚠️ Over Sold' : '🥛 Remaining Milk'}
              </div>
              <div style={{ fontSize: '24px', fontWeight: '700', color: remainingMilk > 0 ? '#e65100' : remainingMilk < 0 ? '#c62828' : '#666' }}>
                {Math.abs(remainingMilk).toFixed(1)} L
              </div>
              {remainingMilk > 0 && <div style={{ fontSize: '10px', color: '#e65100', marginTop: '4px', fontWeight: '600' }}>Available to sell</div>}
              {remainingMilk < 0 && <div style={{ fontSize: '10px', color: '#c62828', marginTop: '4px', fontWeight: '600' }}>Exceeded by {Math.abs(remainingMilk).toFixed(1)} L</div>}
            </div>
          </div>

          {/* ── Cow / Buffalo Leftover Split ── */}
          <div style={{ marginTop: '16px' }}>
            <h4 style={{ fontSize: '14px', fontWeight: '700', color: '#2c3e50', marginBottom: '12px' }}>🐄🐃 Remaining Today by Animal Type</h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
              <div style={{ background: '#e3f2fd', borderRadius: '10px', padding: '16px', border: '2px solid #90caf9' }}>
                <div style={{ fontSize: '13px', fontWeight: '700', color: '#1565c0', marginBottom: '10px' }}>🐄 Cow Milk</div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}><span style={{ fontSize: '12px', color: '#555' }}>Produced</span><span style={{ fontSize: '13px', fontWeight: '600', color: '#1565c0' }}>{splitSummary ? splitSummary.cow.produced.toFixed(1) : totalCow.toFixed(1)} L</span></div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}><span style={{ fontSize: '12px', color: '#555' }}>Sold</span><span style={{ fontSize: '13px', fontWeight: '600', color: '#1565c0' }}>{splitSummary ? splitSummary.cow.sold.toFixed(1) : '—'} L</span></div>
                <div style={{ borderTop: '1px solid #90caf9', paddingTop: '8px', marginTop: '4px', display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: '12px', fontWeight: '700', color: '#1565c0' }}>Remaining</span>
                  <span style={{ fontSize: '16px', fontWeight: '700', color: '#1565c0' }}>{splitSummary ? splitSummary.cow.leftover.toFixed(1) : Math.max(0, totalCow - todayTotalSold).toFixed(1)} L</span>
                </div>
              </div>
              <div style={{ background: '#f3e5f5', borderRadius: '10px', padding: '16px', border: '2px solid #ce93d8' }}>
                <div style={{ fontSize: '13px', fontWeight: '700', color: '#6a1b9a', marginBottom: '10px' }}>🐃 Buffalo Milk</div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}><span style={{ fontSize: '12px', color: '#555' }}>Produced</span><span style={{ fontSize: '13px', fontWeight: '600', color: '#6a1b9a' }}>{splitSummary ? splitSummary.buffalo.produced.toFixed(1) : totalBuffalo.toFixed(1)} L</span></div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}><span style={{ fontSize: '12px', color: '#555' }}>Sold</span><span style={{ fontSize: '13px', fontWeight: '600', color: '#6a1b9a' }}>{splitSummary ? splitSummary.buffalo.sold.toFixed(1) : '—'} L</span></div>
                <div style={{ borderTop: '1px solid #ce93d8', paddingTop: '8px', marginTop: '4px', display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: '12px', fontWeight: '700', color: '#6a1b9a' }}>Remaining</span>
                  <span style={{ fontSize: '16px', fontWeight: '700', color: '#6a1b9a' }}>{splitSummary ? splitSummary.buffalo.leftover.toFixed(1) : Math.max(0, totalBuffalo - todayTotalSold).toFixed(1)} L</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── 4 Sub-Tabs ── */}
      <div style={{ display: 'flex', gap: '0', marginBottom: '0', borderRadius: '12px 12px 0 0', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        {tabs.map((tab, index) => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)} style={{
            flex: 1, padding: '16px 8px',
            background: activeTab === tab.key ? '#2c3e50' : 'white',
            color:      activeTab === tab.key ? 'white'   : '#7f8c8d',
            border: 'none',
            borderRight: index < tabs.length - 1 ? '1px solid #e0e0e0' : 'none',
            borderBottom: activeTab === tab.key ? 'none' : '2px solid #e0e0e0',
            cursor: 'pointer', fontSize: '13px', fontWeight: '700',
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px',
          }}>
            <span>{tab.label}</span>
            <span style={{
              background: activeTab === tab.key ? 'rgba(255,255,255,0.25)' : '#f0f0f0',
              color:      activeTab === tab.key ? 'white' : '#95a5a6',
              padding: '2px 10px', borderRadius: '10px', fontSize: '11px', fontWeight: '600',
            }}>{tab.count} animals</span>
          </button>
        ))}
      </div>

      {/* ── Tab Content with Pagination ── */}
      <div style={{ background: 'white', borderRadius: '0 0 12px 12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', overflow: 'auto', marginBottom: '24px' }}>
        {activeTab === 'today'     && <IndividualTable records={todayRecs}     isAdmin={isAdmin} todayDate={today} onEdit={openEditModal} onDelete={handleDeleteProduction} page={todayPage}     setPage={setTodayPage} />}
        {activeTab === 'yesterday' && <IndividualTable records={yesterdayRecs} isAdmin={isAdmin} todayDate={today} onEdit={openEditModal} onDelete={handleDeleteProduction} page={yesterdayPage} setPage={setYesterdayPage} />}
        {activeTab === 'week'      && <CumulativeTable records={thisWeekRecs}  page={weekPage}  setPage={setWeekPage} />}
        {activeTab === 'month'     && <CumulativeTable records={thisMonthRecs} page={monthPage} setPage={setMonthPage} />}
      </div>

      {/* ── Bulk Entry Modal ── */}
      {showBulkModal && (
        <div style={styles.modalOverlay} onClick={() => setShowBulkModal(false)}>
          <div style={{ ...styles.modal, maxWidth: '700px' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={styles.modalTitle}>{bulkSession === 'Morning' ? '🌅' : '🌙'} {bulkSession} Production Entry</h2>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
                <input
                  type="date" value={bulkDate}
                  max={(() => { const d = new Date(); d.setDate(d.getDate() - 1); return d.toISOString().split('T')[0]; })()}
                  min={(() => { const d = new Date(); d.setDate(d.getDate() - 7); return d.toISOString().split('T')[0]; })()}
                  onChange={(e) => setBulkDate(e.target.value)}
                  style={{ padding: '6px 10px', border: '2px solid #e0e0e0', borderRadius: '8px', fontSize: '13px' }}
                />
                {bulkDate !== today && <span style={{ fontSize: '11px', color: '#e65100', fontWeight: '600' }}>⚠️ Backdate — will go to leftover</span>}
              </div>
            </div>
            <p style={{ color: '#7f8c8d', fontSize: '13px', marginBottom: '16px' }}>Enter quantity for animals that produced milk. Leave blank to skip.</p>
            <div style={{ maxHeight: '400px', overflowY: 'auto', marginBottom: '20px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f8f9fa' }}>
                    <th style={{ ...styles.th, padding: '12px' }}>Tag #</th>
                    <th style={{ ...styles.th, padding: '12px' }}>Animal Name</th>
                    <th style={{ ...styles.th, padding: '12px' }}>Type</th>
                    <th style={{ ...styles.th, padding: '12px' }}>Quantity (L)</th>
                  </tr>
                </thead>
                <tbody>
                  {bulkEntries.map((entry, index) => (
                    <tr key={entry.tag_number} style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td style={{ padding: '10px 12px' }}><span style={styles.tag}>{entry.tag_number}</span></td>
                      <td style={{ padding: '10px 12px', fontWeight: '600' }}>{entry.animal_name}</td>
                      <td style={{ padding: '10px 12px' }}>
                        <span style={animalTypeBadge(entry.animal_type)}>{entry.animal_type || '-'}</span>
                      </td>
                      <td style={{ padding: '10px 12px' }}>
                        <input
                          type="number" step="0.1" min="0"
                          value={entry.quantity}
                          onChange={(e) => handleQuantityChange(index, e.target.value)}
                          placeholder="0.0"
                          style={{ width: '100px', padding: '8px 10px', border: '2px solid #e0e0e0', borderRadius: '6px', fontSize: '14px', textAlign: 'center' }}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ background: '#f8f9fa', padding: '12px 16px', borderRadius: '8px', marginBottom: '20px', fontSize: '13px', color: '#555' }}>
              Entries to save: <strong>{bulkEntries.filter(e => e.quantity !== '' && parseFloat(e.quantity) > 0).length}</strong> of {bulkEntries.length} animals
              &nbsp;|&nbsp; Total: <strong>{bulkEntries.reduce((sum, e) => sum + (parseFloat(e.quantity) || 0), 0).toFixed(1)} L</strong>
            </div>
            <div style={styles.modalButtons}>
              <button type="button" onClick={() => { setShowBulkModal(false); setBulkEntries([]); }} style={styles.cancelButton}>Cancel</button>
              <button type="button" onClick={handleSaveBulk} disabled={saving} style={{ ...styles.submitButton, opacity: saving ? 0.7 : 1 }}>
                {saving ? '⏳ Saving...' : `💾 Save ${bulkSession} Entries`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Edit Modal ── */}
      {showEditModal && selectedProduction && (
        <div style={styles.modalOverlay} onClick={() => { setShowEditModal(false); setSelectedProduction(null); }}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h2 style={styles.modalTitle}>Edit Production Entry</h2>
            <div style={{ background: '#f8f9fa', padding: '16px', borderRadius: '8px', marginBottom: '20px' }}>
              <div style={{ fontSize: '14px', color: '#555', marginBottom: '6px' }}><strong>Animal:</strong> {selectedProduction.animal_name} ({selectedProduction.tag_number})</div>
              <div style={{ fontSize: '14px', color: '#555', marginBottom: '6px' }}><strong>Date:</strong> {formatDate(selectedProduction.date)}</div>
              <div style={{ fontSize: '14px', color: '#555' }}><strong>Session:</strong> {selectedProduction.session}</div>
            </div>
            <form onSubmit={handleEditProduction}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Production (Liters) <span style={{ color: '#e74c3c' }}>*</span></label>
                <input type="number" step="0.1" value={editFormData.production} onChange={(e) => setEditFormData({ production: e.target.value })} style={styles.input} required />
              </div>
              <div style={styles.modalButtons}>
                <button type="button" onClick={() => { setShowEditModal(false); setSelectedProduction(null); }} style={styles.cancelButton}>Cancel</button>
                <button type="submit" style={styles.submitButton}>Update Entry</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Pagination Styles ─────────────────────────────────────────────────────────
const paginationStyles = {
  wrapper:       { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 20px', borderTop: '1px solid #f0f0f0', background: '#fafafa', flexWrap: 'wrap', gap: '10px' },
  info:          { fontSize: '13px', color: '#7f8c8d' },
  controls:      { display: 'flex', alignItems: 'center', gap: '6px' },
  btn:           { padding: '6px 12px', border: '1px solid #ddd', borderRadius: '6px', background: 'white', cursor: 'pointer', fontSize: '13px', fontWeight: '600', color: '#2c3e50' },
  btnDisabled:   { opacity: 0.4, cursor: 'not-allowed' },
  pageIndicator: { padding: '6px 14px', background: '#2c3e50', color: 'white', borderRadius: '6px', fontSize: '13px', fontWeight: '600' },
};

// ── Component Styles ──────────────────────────────────────────────────────────
const styles = {
  morningButton:    { padding: '10px 20px', background: '#f39c12', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
  eveningButton:    { padding: '10px 20px', background: '#8e44ad', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
  todayContainer:   { background: 'white', borderRadius: '12px', padding: '24px', marginBottom: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' },
  todayTitle:       { fontSize: '18px', fontWeight: '700', color: '#2c3e50', margin: '0 0 20px 0' },
  summaryGrid:      { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' },
  summaryCard:      { padding: '20px', borderRadius: '12px' },
  summaryAnimalTitle: { fontSize: '16px', fontWeight: '700', color: '#2c3e50', marginBottom: '12px' },
  summaryRow:       { display: 'flex', justifyContent: 'space-between', marginBottom: '8px' },
  summaryLabel:     { fontSize: '14px', color: '#666' },
  summaryValue:     { fontSize: '14px', fontWeight: '600', color: '#2c3e50' },
  summaryTotal:     { marginTop: '12px', paddingTop: '12px', borderTop: '2px solid rgba(0,0,0,0.1)', fontSize: '16px', fontWeight: '700', color: '#2c3e50' },
  emptySection:     { padding: '40px 20px', textAlign: 'center', color: '#95a5a6', fontSize: '14px' },
  table:            { width: '100%', borderCollapse: 'collapse' },
  headerRow:        { background: '#f8f9fa' },
  th:               { padding: '14px 16px', textAlign: 'left', fontSize: '13px', fontWeight: '700', color: '#2c3e50', borderBottom: '2px solid #e0e0e0', whiteSpace: 'nowrap' },
  row:              { borderBottom: '1px solid #f0f0f0' },
  td:               { padding: '14px 16px', fontSize: '14px', color: '#2c3e50' },
  tag:              { background: '#3498db', color: 'white', padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: '700' },
  editButton:       { padding: '4px 8px', background: '#3498db', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '11px' },
  deleteButton:     { padding: '4px 8px', background: '#e74c3c', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '11px' },
  retryButton:      { marginTop: '20px', padding: '12px 24px', background: '#3498db', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
  modalOverlay:     { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, overflow: 'auto', padding: '20px' },
  modal:            { background: 'white', padding: '30px', borderRadius: '12px', width: '90%', maxWidth: '500px', boxShadow: '0 10px 40px rgba(0,0,0,0.2)', maxHeight: '90vh', overflow: 'auto' },
  modalTitle:       { fontSize: '22px', fontWeight: '700', marginBottom: '0', color: '#2c3e50' },
  formGroup:        { display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '16px' },
  label:            { fontSize: '13px', fontWeight: '600', color: '#2c3e50' },
  input:            { width: '100%', padding: '10px 12px', border: '2px solid #e0e0e0', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' },
  modalButtons:     { display: 'flex', gap: '12px', marginTop: '24px' },
  cancelButton:     { flex: 1, padding: '12px', background: '#95a5a6', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
  submitButton:     { flex: 1, padding: '12px', background: '#3498db', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
};

export default ProductionTab;